import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  useAddGenes,
  useCreateGenePanel,
  useGetGenePanel,
  usePatchGenePanel,
} from '@/api/genes/genes';
import { type GenePanelCartPayload, genePanelCartPayload } from '@/zod_schemas';
import { getErrorMessage } from '@/utils/getErrorMessage';

type GenePanelParams = {
  panelId: string;
};

interface UseGenePanelFromHandlerReturn
  extends ReturnType<typeof useForm<GenePanelCartPayload>> {
  mode: 'create' | 'update';
  panelId?: string;
}

type UseGenePanelFormHandler = () => UseGenePanelFromHandlerReturn;

export const useGenePanelFormHandler: UseGenePanelFormHandler = () => {
  const { panelId } = useParams<GenePanelParams>();
  const { data } = useGetGenePanel(panelId);
  const formMethods = useForm<GenePanelCartPayload>({
    resolver: zodResolver(genePanelCartPayload),
    defaultValues: data || {},
  });

  return {
    ...formMethods,
    mode: panelId ? 'update' : 'create',
    panelId,
  };
};

function processOutputData(formData: GenePanelCartPayload) {
  const genes = formData.genes ?? [];
  // guard + de-dupe by HGNC id
  const seen = new Set<string>();
  return genes
    .map((g) => ({
      gene_id: g?.gene?.hgnc_id, // backend expects HGNC:XXXX
      notes: (g?.notes ?? '').trim(),
    }))
    .filter(
      (g) => !!g.gene_id && !seen.has(g.gene_id) && (seen.add(g.gene_id), true),
    );
}

export const useCreateOrUpdateGenePanel = (
  mode: 'create' | 'update',
  panelId?: string,
) => {
  const createGenePanel = useCreateGenePanel();
  const addGenes = useAddGenes();
  const patchGenePanel = usePatchGenePanel();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<GenePanelCartPayload> = useCallback(
    async (formData) => {
      if (mode === 'create') {
        try {
          // 1) create
          const { name, description } = formData;
          const createResp = await createGenePanel.mutateAsync({
            name,
            description,
          });

          const newPanelId = (
            'data' in createResp ? createResp.data : createResp
          ).id as string;

          if (!newPanelId) {
            throw new Error(
              'Panel created but ID was not returned by the server.',
            );
          }

          // 2) add genes (best-effort)
          const genesPayload = processOutputData(formData);
          if (genesPayload.length) {
            try {
              await addGenes.mutateAsync({
                panelId: newPanelId,
                genes: genesPayload,
              });
            } catch {
              enqueueSnackbar('Panel created but failed to add genes', {
                variant: 'warning',
              });
            }
          }

          enqueueSnackbar('Gene panel created successfully', {
            variant: 'success',
          });
          navigate(`/administrator/genepanel/${newPanelId}`);
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
        }
        return;
      }

      // -------- UPDATE mode ----------
      if (!panelId) {
        enqueueSnackbar('Gene Panel ID is required. Failed to update.', {
          variant: 'error',
        });
        return;
      }

      // 1) Patch name/description first
      try {
        const patchPayload: Partial<
          Pick<GenePanelCartPayload, 'name' | 'description' | 'version'>
        > = {};
        if (formData.name?.trim()) patchPayload.name = formData.name.trim();
        if (formData.description?.trim()) {
          patchPayload.description = formData.description.trim();
        }
        if (formData.version?.trim?.()) {
          patchPayload.version = formData.version.trim();
        }

        if (Object.keys(patchPayload).length > 0) {
          await patchGenePanel.mutateAsync({
            id: panelId,
            payload: patchPayload,
          });
        }
      } catch (err) {
        // abort if PATCH failed
        enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
        return;
      }

      // 2) Then add genes (best-effort)
      try {
        const genesPayload = processOutputData(formData);
        if (genesPayload.length) {
          await addGenes.mutateAsync({ panelId, genes: genesPayload });
          enqueueSnackbar('Panel details updated and genes added', {
            variant: 'success',
          });
        } else {
          enqueueSnackbar('Panel details updated', { variant: 'success' });
        }
      } catch (err) {
        // edits succeeded, adding genes failed -> warn, don’t fail whole flow
        enqueueSnackbar('Panel updated but failed to add some genes', {
          variant: 'warning',
        });
      }
    },
    [mode, panelId, createGenePanel, addGenes, patchGenePanel, navigate],
  );

  return onSubmit;
};
